import { gql, useQuery } from '@apollo/client';
import RemainingRevenueDistributionThumbnail from '@assets/images/report-components/remaining-project-revenue-distribution-thumbnail.png';
import { useTheme } from '@mui/material';
import { useMemo, useState } from 'react';
import { ComponentBox } from '@/admins/SmartReports/components/Components/Common/ComponentBox';
import { ComponentTitle } from '@/admins/SmartReports/components/Components/Common/ComponentTitle';
import { SmartReportComponentTypesEnum, SmartReportTypeEnum } from '@/admins/SmartReports/types';
import Graph from '@/components/Graphs/graph';
import {
  AssetAggregateByEnum,
  type AssetAggregates,
  AssetAggregatesFieldsEnum,
} from '@/types/GraphqlSchema';
import { createDateWithYearOffset } from '@/utils/dateFormatter';
import { getNestedValue } from '@/utils/helpers';
import { LoadingErrorWrapper } from '../Common/LoadingErrorWrapper';
import type {
  ComponentDefinition,
  ComponentEditProps,
  ComponentViewProps,
  UseDataType,
} from '../types';
import { getLayout } from './helpers';

const QUERY = gql`
  query assetAggregates($input: AssetAggregatesInput!) {
    assetAggregates(input: $input) {
      remainingConsortiumSum
      remainingCapexAbexSum
      remainingOpexSum
      remainingGovernmentRevenueSum
    }
  }
`;

export const COMPONENT_ID = 'remaining_project_revenue_distribution';

const useData = ({ portfolio, persistedData, onComponentLoad }: UseDataType<AssetAggregates[]>) => {
  const [chartDataFetched, setChartDataFetched] = useState(persistedData);

  const { loading, error } = useQuery<{ assetAggregates: AssetAggregates[] }>(QUERY, {
    variables: {
      input: {
        by: AssetAggregateByEnum.Asset,
        fields: [
          AssetAggregatesFieldsEnum.RemainingConsortiumSum,
          AssetAggregatesFieldsEnum.RemainingCapexAbexSum,
          AssetAggregatesFieldsEnum.RemainingOpexSum,
          AssetAggregatesFieldsEnum.RemainingGovernmentRevenueSum,
        ],
        portfolio: {
          ...portfolio,
          startDate: createDateWithYearOffset(0),
          endDate: createDateWithYearOffset(100), // Far future date to include all remaining cash flows
        },
      },
    },
    skip: !!persistedData,
    onCompleted: response => {
      if (response?.assetAggregates) {
        const chartData = response.assetAggregates;
        setChartDataFetched(chartData);
        if (onComponentLoad) {
          onComponentLoad({
            componentId: COMPONENT_ID,
            data: chartData,
          });
        }
      }
    },
  });

  return { chartData: chartDataFetched, error, loading };
};

const useFormattedData = (data: AssetAggregates[]) => {
  const theme = useTheme();

  return useMemo(() => {
    if (!Array.isArray(data) || data.length === 0 || data[0] == null) return null;

    const chartData = data[0];
    try {
      const stacks = [
        {
          label: 'Consortium',
          key: AssetAggregatesFieldsEnum.RemainingConsortiumSum,
          color: 'deepSeaNavy.c100',
        },
        {
          label: 'Government',
          key: AssetAggregatesFieldsEnum.RemainingGovernmentRevenueSum,
          color: 'fireOrange.c100',
        },
        {
          label: 'Capex incl. abandonment',
          key: AssetAggregatesFieldsEnum.RemainingCapexAbexSum,
          color: 'earthGreen.c100',
        },
        {
          label: 'Opex',
          key: AssetAggregatesFieldsEnum.RemainingOpexSum,
          color: 'amber.c100',
        },
      ];

      const total = stacks.reduce((sum, stack) => {
        const value = chartData[stack.key];
        return sum + (value != null ? value : 0);
      }, 0);

      // Create bar traces for each stack (each field is a trace)
      return stacks.map(stack => {
        const x = ['']; // Single bar for total distribution, no x-axis categories
        const value = chartData[stack.key] ?? 0;
        const y = [value];
        const color = getNestedValue(theme, `palette.chartColors.${stack.color}`);
        const percentage = total > 0 ? ((value / total) * 100).toFixed(2) : '0.00';
        const textValue = `US$${value.toFixed(2)}MM (${percentage}%)`;

        return {
          x,
          y,
          type: 'bar',
          name: stack.label,
          marker: { color },
          text: [textValue],
          texttemplate: '%{text}',
          textposition: 'inside',
          insidetextanchor: 'middle',
          textfont: { size: 16, color: '#FFFFFF', weight: 'bold' },
          legendrank: stacks.length - stacks.indexOf(stack),
        };
      });
    } catch {
      return null;
    }
  }, [data, theme]);
};

const ComponentView = ({ data }: ComponentViewProps<AssetAggregates[]>) => {
  const formattedData = useFormattedData(data);
  if (!formattedData) return null;

  return (
    <ComponentBox>
      <ComponentTitle>Distribution (NPV10) of Remaining Project Revenue</ComponentTitle>
      <Graph
        data={formattedData}
        layout={getLayout({
          yaxis: { title: { text: 'US$MM' } },
          margin: { b: 50 },
          legend: { y: -0.05 },
          barmode: 'stack',
          bargap: 0.4,
          bargroupgap: 0.1,
        })}
        title={undefined}
        config={undefined}
        xlabel={undefined}
        ylabel={undefined}
        loading={undefined}
        onUpdate={undefined}
        onClick={undefined}
        onRelayout={undefined}
        onInitialized={undefined}
        onAfterPlot={undefined}
        maxY={undefined}
        darkTheme={undefined}
        context={undefined}
      />
    </ComponentBox>
  );
};

const ComponentEdit = ({
  portfolio,
  persistedData,
  onComponentLoad,
}: ComponentEditProps<AssetAggregates[]>) => {
  const { chartData, loading, error } = useData({
    portfolio,
    persistedData,
    onComponentLoad,
  });

  return (
    <LoadingErrorWrapper loading={loading} error={error} data={chartData}>
      {chartData && <ComponentView data={chartData} />}
    </LoadingErrorWrapper>
  );
};

const ComponentThumbnail = () => (
  <ComponentBox>
    <ComponentTitle sx={{ fontSize: '0.25rem', marginBottom: 0 }}>
      Distribution (NPV10) of Remaining Project Revenue
    </ComponentTitle>
    <img
      src={RemainingRevenueDistributionThumbnail}
      alt='Distribution (NPV10) of Remaining Project Revenue Chart Thumbnail'
      style={{ width: '100%', height: 'auto' }}
    />
  </ComponentBox>
);

export const RemainingProjectRevenueDistributionComponent: ComponentDefinition<AssetAggregates[]> =
  {
    id: COMPONENT_ID,
    name: 'Distribution (NPV10) of Remaining Project Revenue',
    type: SmartReportComponentTypesEnum.Chart,
    reportTypes: [SmartReportTypeEnum.Asset],
    Thumbnail: ComponentThumbnail,
    Component: ComponentEdit,
    ComponentView,
  };
